import { useRef, useEffect } from "react";
import { useFeatureFlag } from "../hooks/useFeatureFlags";
import { FEATURE_FLAGS } from "./configCatUtils";
import { track, getAnonymousId } from "./rudderstack";
import { safeLocalStorage, safeSessionStorage } from "./safeStorage";

// Study-specific configuration for A/B tests
interface StudyABTestConfig {
  studyId: string;
  featureFlag: string;
  experimentName: string;
  sessionStorageKey: string;
}

// Configuration for all studies that have AI medication variants
const STUDY_CONFIGS: Record<string, StudyABTestConfig> = {
  covid: {
    studyId: "covid",
    featureFlag: FEATURE_FLAGS.COVID_AI_MEDICATIONS,
    experimentName: "covid_ai_medications",
    sessionStorageKey: "covid_variant_tracked",
  },
  vt2d: {
    studyId: "vt2d",
    featureFlag: FEATURE_FLAGS.VIKING_T2D_AI_MEDICATIONS,
    experimentName: "viking_t2d_ai_medications",
    sessionStorageKey: "viking_t2d_variant_tracked",
  },
};

/**
 * Generic hook for handling A/B test assignment for medication entry features
 * Uses ConfigCat's built-in percentage targeting via boolean flags
 */
export function useStudyMedicationVariant(landingPageId: string): {
  variant: "control" | "ai";
  isLoading: boolean;
} {
  // Use ref to ensure tracking only happens once, even with StrictMode
  const hasTracked = useRef(false);
  
  // Check if the base feature is enabled
  const isAIEnabled = useFeatureFlag(FEATURE_FLAGS.AI_MEDICATION_ENTRY, false);
  
  // Get study configuration
  const studyConfig = STUDY_CONFIGS[landingPageId];
  
  // Get the feature flag value - ConfigCat handles percentage targeting internally
  const featureFlagValue = useFeatureFlag(
    studyConfig?.featureFlag || "", 
    false
  );
  
  // Determine the variant based on conditions
  let variant: "control" | "ai" = "control";
  let shouldTrack = false;
  
  if (!studyConfig) {
    console.warn(`[A/B Test] No study configuration found for landingPageId: ${landingPageId}`);
  } else if (!isAIEnabled) {
    console.log(`[A/B Test] AI medication entry disabled globally for study: ${landingPageId}`);
  } else {
    // ConfigCat returns true/false based on its percentage targeting rules
    variant = featureFlagValue ? "ai" : "control";
    shouldTrack = true;
  }
  
  // Get both anonymous IDs for tracking
  const rudderstackUserId = getAnonymousId() || "anonymous";
  const configCatUserId = safeLocalStorage.getItem("configcat_anonymous_id") || "unknown";
  
  // Use effect to handle tracking with proper cleanup
  useEffect(() => {
    // Only track if we should and haven't already (handles StrictMode double-render)
    if (shouldTrack && studyConfig && !hasTracked.current && !safeSessionStorage.getItem(studyConfig.sessionStorageKey)) {
      console.log(`[A/B Test] Assigning variant for ${studyConfig.experimentName}:`, {
        variant,
        configCatUserId,
        rudderstackUserId,
        featureFlagValue,
      });
      
      track("lp.experiment_variant_assigned", {
        experiment: studyConfig.experimentName,
        variant,
        landingPageId,
        userId: rudderstackUserId,
        configCatUserId,
        timestamp: new Date().toISOString(),
      });
      
      safeSessionStorage.setItem(studyConfig.sessionStorageKey, "true");
      hasTracked.current = true;
    }
  }, [variant, studyConfig, landingPageId, rudderstackUserId, configCatUserId, featureFlagValue, shouldTrack]);
  
  return { variant, isLoading: false };
}

/**
 * Legacy hook for COVID medication variant - maintained for backward compatibility
 * @deprecated Use useStudyMedicationVariant instead
 */
export function useCovidMedicationVariant(landingPageId: string): {
  variant: "control" | "ai";
  isLoading: boolean;
} {
  return useStudyMedicationVariant(landingPageId);
}

/**
 * Returns the appropriate prescreening questions based on study and variant
 */
export function getScreenerQuestions(studyId: string, variant: "control" | "ai"): string {
  const screenerMap: Record<string, { control: string; ai: string }> = {
    covid: {
      control: "covidVaccineScreeningQuestions",
      ai: "covidVaccineScreeningQuestionsAI",
    },
    vt2d: {
      control: "vikingT2dScreeningQuestions",
      ai: "vikingT2dScreeningQuestionsAI",
    },
  };
  
  const studyScreeners = screenerMap[studyId];
  if (!studyScreeners) {
    throw new Error(`No screener configuration found for study: ${studyId}`);
  }
  
  return studyScreeners[variant];
}

/**
 * Legacy function for COVID screener questions - maintained for backward compatibility
 * @deprecated Use getScreenerQuestions instead
 */
export function getCovidScreenerQuestions(variant: "control" | "ai") {
  return getScreenerQuestions("covid", variant);
}

/**
 * Analytics helper to track medication entry events with variant info
 */
export function trackMedicationEvent(
  eventName: string, 
  properties: Record<string, any>, 
  variant: "control" | "ai",
  studyId: string = "covid" // Default to covid for backward compatibility
) {
  const studyConfig = STUDY_CONFIGS[studyId];
  const experiment = studyConfig?.experimentName || `${studyId}_ai_medications`;
  
  track(eventName, {
    ...properties,
    variant,
    experiment,
    studyId,
    timestamp: new Date().toISOString()
  });
}